import { useUpsertConsultantNote } from '@/api/consultants';
import useConsult from '@/hooks/useConsult';
import { pageNameBySlug } from '@/utils/constants';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NotesModal from './NotesModal';

type ConsultantAddNoteModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

function getNotesByDate(notes?: Api.Consultant['notes']): Api.NotesByDate {
  if (!notes || Array.isArray(notes)) {
    return {};
  }

  return notes;
}

function ConsultantAddNoteModal({ isOpen, setIsOpen }: ConsultantAddNoteModalProps) {
  const location = useLocation();
  const { activeConsultant, updateConsultantPartners } = useConsult();
  const upsertNoteMutation = useUpsertConsultantNote();

  const [content, setContent] = useState('');
  const pathSlug = (() => {
    const segments = location.pathname.split('/').filter(Boolean);
    // Prefer third segment if present (e.g., /personal/pinnacle -> 'pinnacle')
    if (segments[2]) return segments[2];
    if (segments[1]) return segments[1];
    return location.pathname || 'home';
  })();

  const todayKey = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`; // canonical zero-padded, e.g., 2025-09-15
  })();

  // Pre-fill with the existing note for today + this page so that saving edits
  // it instead of silently overwriting it (the backend upserts on date+path).
  useEffect(() => {
    if (!isOpen) return;
    const existing = getNotesByDate(activeConsultant?.notes)[todayKey]?.[pathSlug] || '';
    setContent(existing);
  }, [isOpen, activeConsultant, todayKey, pathSlug]);

  const handleSave = async () => {
    if (!activeConsultant) return;
    if (!content.trim()) return;

    const existingNotes = getNotesByDate(activeConsultant.notes);
    const dateNotes = existingNotes[todayKey] || {};
    const updatedDateNotes = { ...dateNotes, [pathSlug]: content.trim() };
    const updatedNotes: Api.NotesByDate = { ...existingNotes, [todayKey]: updatedDateNotes };

    const updatedConsultant: Api.Consultant = {
      ...activeConsultant,
      notes: updatedNotes,
    };

    await upsertNoteMutation.mutateAsync({
      consultantId: activeConsultant.id,
      note: {
        dateKey: todayKey,
        pathKey: pathSlug,
        value: content.trim(),
      },
    });

    // Update context immediately
    updateConsultantPartners(updatedConsultant);

    setContent('');
    setIsOpen(false);
  };

  return (
    <NotesModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Agregar Nota"
      size="large"
    >
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-main-50 to-secondary-50 rounded-xl p-4 border border-main-200 text-sm text-gray-700">
          <div>
            <span className="font-semibold">Fecha:</span>
            {' '}
            {todayKey}
          </div>
          <div>
            <span className="font-semibold">Página:</span>
            {' '}
            {pageNameBySlug({ name: pathSlug })}
          </div>
        </div>
        <div>
          <div className="block text-sm font-medium text-gray-700 mb-1">Contenido</div>
          <textarea
            id="note-content"
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-main-500 min-h-[120px]"
            placeholder="Escribe la nota aquí..."
            aria-label="Contenido de la nota"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="btn btn-white"
            onClick={() => setIsOpen(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn"
            disabled={!content.trim() || !activeConsultant}
            onClick={handleSave}
          >
            Guardar Nota
          </button>
        </div>
      </div>
    </NotesModal>
  );
}

export default ConsultantAddNoteModal;
